import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@mui/material";
import MDBox from "mdComponents/MDBox";
import MDTypography from "mdComponents/MDTypography";
import { useUser } from "../context/UserContext";
import { useHostPortalContext } from "../context/HostPortalContext";
import { useAlertContext } from "../context/AlertContext";
import corporateAccountService from "../services/corporateAccountService";
import locationsService from "../services/locationsService";
import { CorporateHostRating } from "../models/corporateHostRatingModel";
import LoadingSpinner from "./LoadingSpinner";
import { MESSAGES_ERROR } from "../constants";


interface CorporateRatingsProps {
  limitToId?: number;
  hideBusinessName?: boolean;
  title?: string;
  description?: string;
  disableClick?: boolean;
}

const CorporateRatings = (props: CorporateRatingsProps) => {
  const { limitToId, description, title, hideBusinessName, disableClick } = props;
  const navigate = useNavigate();
  const { setHost } = useHostPortalContext();
  const { setAlert } = useAlertContext();
  const { userCorporateAccount } = useUser();

  const [ratings, setRatings] = useState<CorporateHostRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [showCorporateRollup, setShowCorporateRollup] = useState(true);

  useEffect(() => {
    const loadRatings = async () => {
      try {
        setLoading(true);
        let data = await corporateAccountService.getCorporateHostRatings(
          userCorporateAccount?.id
        );

        if (data) {
            if(limitToId){
                data = data.filter(a => a.hostId === limitToId);
            };

          setRatings(data);
          setShowCorporateRollup(data.filter(a => a.hostId !== -1).length > 1);
        } else {
          setAlert("error", "Error", MESSAGES_ERROR.ERROR_LOADING_CORPORATE_ACCOUNT_HOST_USERS);
        }
      } catch (err) {
        console.error(err);
        setAlert("error", "Error", MESSAGES_ERROR.ERROR_LOADING_CORPORATE_ACCOUNT_HOST_USERS);
      } finally {
        setLoading(false);
      }
    };

    loadRatings();
  }, []);

  const toGoLocationRatings = async (hostId: number) => {
    if (disableClick || hostId === -1) return;

    setRedirecting(true);

    const host = await locationsService.getHostRecord(hostId);
    if (!host) {
      setRedirecting(false);
      return;
    }

    setHost(host);
    setRedirecting(false);

    navigate(`/location/ratings/${hostId}`);
  };

  const renderCard = (item: CorporateHostRating) => {
    const isCorporateAverage = item.hostId === -1;
    if (isCorporateAverage && !showCorporateRollup) return null;

    return (
      <Card
        key={item.hostId}
        onClick={() => toGoLocationRatings(item.hostId)}
        sx={{
          mb: 2,
          cursor: isCorporateAverage ? "default" : "pointer",
          padding: 2,
          border: isCorporateAverage ? "2px solid #21b2a6" : "1px solid #ddd",
          background: isCorporateAverage ? "#e8fffd" : "#fff",
        }}
      >
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
            {!hideBusinessName &&
          <MDTypography variant="h6" fontWeight="bold" color={isCorporateAverage ? "black" : "text"}>
            {item.hostName}
          </MDTypography>}

          <MDBox textAlign="right">
            <MDTypography variant="h6" color={isCorporateAverage ? "black" : "text"}>
              ⭐ {item.avgRating?.toFixed(2) ?? "0.00"}
            </MDTypography>
            <MDTypography variant="caption" color={isCorporateAverage ? "black" : "text"}>
              ({item.totalRatings ?? 0} ratings)
            </MDTypography>
          </MDBox>
        </MDBox>
      </Card>
    );
  };

  if (loading) return <LoadingSpinner title="Loading Ratings..." />;
  return (
    <MDBox py={0}>
      <MDTypography variant="h5" fontWeight="bold">
        {title ?? 'Live Ratings'}
      </MDTypography>
       <MDTypography variant="button" color="text" mb={1} sx={{lineHeight: '1.3', display: 'block', whiteSpace: 'normal',}}> 
        {description ?? 'Ratings are calculated based on user ratings after use. Click a location to view more details.'}
      </MDTypography>
      {ratings.map(item => renderCard(item))}
    </MDBox>
  );
};

export default CorporateRatings;
